
import Vue from 'vue';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import { RUNNIT_NODE_RUN_RESULT_TYPE, RUNNIT_NODE_DEF_TOOL_APP_TYPE, RunnitNodeDef } from '@run-diffusion/shared';
import DatePicker from '@/components/base/DatePicker.vue';
import { mapState } from 'vuex';
import { TeamUserMixin } from '@/mixins';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import _debounce from 'lodash/debounce';
import _sortBy from 'lodash/sortBy';
import _cloneDeep from 'lodash/cloneDeep';
import { fuzzyFilter } from '@/utils/fuzzySearch';
import _trim from 'lodash/trim';
import GlassButton from '@/components/base/GlassButton.vue';

interface ToolFilter {
	id: string
	label: string
	description?: string
	value: boolean
}

export default Vue.extend({
	name: 'ImageGalleryFilterMenu',
	mixins: [
		TeamUserMixin,
	],
	props: {
		defaultToolSelection: { type: String, default: null },
	},
	data () {
		return {
			RUNNIT_NODE_RUN_RESULT_TYPE,
			filterGroups: [
				{
					id: 'user-selector',
					label: 'User',
					icon: 'mdi-account-outline',
				},
				// TODO: add media type filter when we have a better way to filter by media type (postgres query)
				// {
				// 	id: 'media-type',
				// 	label: 'Media type',
				// 	icon: 'mdi-multimedia',
				// },
				{
					id: 'tool-selector',
					label: 'Tool selector',
					icon: 'mdi-tools',
				},
				// TODO: add date range filter when we have a better way to filter by date (postgres query)
				// {
				// 	id: 'date-range',
				// 	label: 'Date range',
				// 	icon: 'mdi-calendar-outline',
				// },
			],
			selectedFilterGroup: 'user-selector',

			defaultMediaFilters: {
				[RUNNIT_NODE_RUN_RESULT_TYPE.IMG]: {
					id: RUNNIT_NODE_RUN_RESULT_TYPE.IMG,
					label: 'Image',
					value: true,
				},
				[RUNNIT_NODE_RUN_RESULT_TYPE.VID]: {
					id: RUNNIT_NODE_RUN_RESULT_TYPE.VID,
					label: 'Video',
					value: true,
				},
				[RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D]: {
					id: RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D,
					label: '3D',
					value: true,
				},
			},
			mediaFilters: {
				[RUNNIT_NODE_RUN_RESULT_TYPE.IMG]: {
					id: RUNNIT_NODE_RUN_RESULT_TYPE.IMG,
					label: 'Image',
					value: true,
				},
				[RUNNIT_NODE_RUN_RESULT_TYPE.VID]: {
					id: RUNNIT_NODE_RUN_RESULT_TYPE.VID,
					label: 'Video',
					value: true,
				},
				[RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D]: {
					id: RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D,
					label: '3D',
					value: true,
				},
			},

			defaultDateRangeFilters: {
				startDate: null,
				endDate: null,
			},
			dateRangeFilters: {
				startDate: null,
				endDate: null,
			},

			defaultToolFilters: {} as Record<string, ToolFilter>,
			toolFilters: {} as Record<string, ToolFilter>,

			defaultUserFilters: {},
			userFilters: {},

			toolSearchValue: '',
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'publicRunnitNodeDefs',
			'loadingPublicRunnitNodeDefs',
			'companyRunnitNodeDefs',
			'loadingCompanyRunnitNodeDefs',
		]),
		selectedToolFilters (): ToolFilter[] {
			return (Object.values(this.toolFilters) as ToolFilter[]).filter((tool: ToolFilter) => tool.value);
		},
		filteredToolFilters (): ToolFilter[] {
			if (!this.toolSearchValue) {
				return Object.values(this.toolFilters) as ToolFilter[];
			}

			// Convert tool filters to the format expected by fuzzyFilter
			const toolsForFuzzy = (Object.values(this.toolFilters) as ToolFilter[]).map((tool: ToolFilter) => ({
				title: tool.label,
				description: tool.description || '',
			}));

			const filteredTools = fuzzyFilter(
				toolsForFuzzy,
				this.toolSearchValue,
				[
					{ name: 'title', weight: 0.7 },
					{ name: 'description', weight: 0.3 }
				]
			);

			// Map back to the original tool filters
			return (Object.values(this.toolFilters) as ToolFilter[]).filter((tool: ToolFilter) =>
				filteredTools.some(filteredTool => filteredTool.title === tool.label)
			);
		}
	},
	mounted () {
		this.debouncedEmitFiltersChange();
	},
	watch: {
		mediaFilters: {
			deep: true,
			handler (val) {
				this.$emit('input', val);
				this.$emit('change', val);
			},
		},
		loadingPublicRunnitNodeDefs: {
			immediate: true,
			handler (newVal) {
				if (newVal === false) {
					this.debouncedSetAvailableRunnitNodeDefs();
				}
			},
		},
		loadingCompanyRunnitNodeDefs: {
			immediate: true,
			handler (newVal) {
				if (newVal === false) {
					this.debouncedSetAvailableRunnitNodeDefs();
				}
			},
		},
	},
	methods: {
		debouncedSetAvailableRunnitNodeDefs: _debounce(function () {
			this.setAvailableRunnitNodeDefs();
		}, 100),
		debouncedEmitFiltersChange: _debounce(function () {
			this.emitFiltersChange();
		}, 1000),
		updateMediaFilter (key: keyof typeof RUNNIT_NODE_RUN_RESULT_TYPE, value: boolean) {
			this.mediaFilters = { ...this.mediaFilters, [key]: { ...this.mediaFilters[key], value } };
			this.debouncedEmitFiltersChange();
		},
		updateDateFilter (key: 'startDate' | 'endDate', value: string) {
			this.dateRangeFilters = { ...this.dateRangeFilters, [key]: value };
			this.debouncedEmitFiltersChange();
		},
		updateToolFilter (id: string, value: boolean) {
			// TODO: because we are using an 'in' filter for the state, we cannot have an in to select multiple tools
			// in the future we should be able to do a multi select with a postgres query
			// this.toolFilters = this.toolFilters.map((tool) => ({ ...tool, value: tool.id === id ? value : false }));
			this.toolFilters = Object.keys(this.toolFilters).reduce((acc, key) => {
				acc[key] = { ...this.toolFilters[key], value: key === id ? value : /*this.toolFilters[key].value */ false };
				return acc;
			}, {});
			this.debouncedEmitFiltersChange();
		},
		updateUserFilters (key: 'email', value: string) {
			this.userFilters = { ...this.userFilters, [key]: _trim(value).toLowerCase() };
			this.debouncedEmitFiltersChange();
		},
		emitFiltersChange () {
			this.$emit('on-filters-change', { mediaFilters: this.mediaFilters, dateRangeFilters: this.dateRangeFilters, toolFilters: this.selectedToolFilters, userFilters: this.userFilters });
		},
		setAvailableRunnitNodeDefs () {
			const publicDefs: RunnitNodeDef[] = this.publicRunnitNodeDefs || [];
			const companyDefs: RunnitNodeDef[] = this.companyRunnitNodeDefs || [];
			const all: RunnitNodeDef[] = [...publicDefs, ...companyDefs];

			const filteredNodeDefs: RunnitNodeDef[] = all.filter((nodeDef: RunnitNodeDef) => {
				const matchesAppType: boolean = nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT;
				const matchesPublished: boolean = this.user?.isAdmin ? true : !!nodeDef.publishedAt;
				const matchesNotBlocked: boolean = !this.isNodeDefBlockedForTeamUser(nodeDef);
				const matchesCompany: boolean = !!(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this.team?.companyId && nodeDef.companyIds?.[this.team.companyId]);
				return matchesAppType && matchesPublished && matchesNotBlocked && matchesCompany;
			});

			const sortedByTitleAsc: RunnitNodeDef[] = _sortBy(filteredNodeDefs, ({ title }) => (title || '').toLowerCase());
			this.toolFilters = sortedByTitleAsc.reduce((acc: Record<string, ToolFilter>, def: RunnitNodeDef) => {
				acc[def.id] = { id: def.id, label: def.title, description: def.description, value: this.defaultToolSelection === def.id };
				return acc;
			}, {});
			this.defaultToolFilters = _cloneDeep(this.toolFilters);
		},
		clearSelectedFilterGroup (id: string) {
			switch (id) {
				case 'user-selector':
					this.userFilters = _cloneDeep(this.defaultUserFilters);
					break;
				case 'media-type':
					this.mediaFilters = _cloneDeep(this.defaultMediaFilters);
					break;
				case 'tool-selector':
					this.toolFilters = _cloneDeep(this.defaultToolFilters);
					break;
				case 'date-range':
					this.dateRangeFilters = _cloneDeep(this.defaultDateRangeFilters);
					break;
			}
			this.debouncedEmitFiltersChange();
		},
		isFilterGroupChanged (id: string) {
			switch (id) {
				case 'user-selector':
					return this.isUserFiltersChanged();
				case 'media-type':
					return this.isMediaFiltersChanged();
				case 'tool-selector':
					return this.isToolFiltersChanged();
				case 'date-range':
					return this.isDateRangeFiltersChanged();
			}
		},
		isUserFiltersChanged () {
			return JSON.stringify(this.userFilters) !== JSON.stringify(this.defaultUserFilters);
		},
		isMediaFiltersChanged () {
			return JSON.stringify(this.mediaFilters) !== JSON.stringify(this.defaultMediaFilters);
		},
		isToolFiltersChanged () {
			return JSON.stringify(this.toolFilters) !== JSON.stringify(this.defaultToolFilters);
		},
		isDateRangeFiltersChanged () {
			return JSON.stringify(this.dateRangeFilters) !== JSON.stringify(this.defaultDateRangeFilters);
		},
	},
	components: {
		MenuOrBottomSheet,
		DatePicker,
		GlassButton,
	},
});
